import { isoDay } from 'helper/timeLabels';

/**
 * The CRM side of the companies.
 *
 * The list (POST admin/crm/companies) comes from the api: `companiesQuery`
 * is what the page sends, `companiesPage` what it reads of the answer.
 */

export const COMPANIES_PER_PAGE = 20;
export const NO_CRM_FILTERS = { action: 'all', commercial: 'all', unassigned: false };

// The filters of the page, as admin/crm/companies reads them: the api
// answers 400 INVALID_FILTERS to a value it does not know, so an empty one is
// sent as the api's « none » ('' or false) and the minimum only with its mode.
// The search is cut to the api's 100 characters and a minimum it would refuse
// (negative, decimal) is left out, so a typo never turns the list into a load
// error.
const MAX_SEARCH_LENGTH = 100;
const MAX_MISSION_COUNT_MIN = 999999;

const missionCountMinOf = ({ missionCount, missionCountMin }) => {
  if (missionCount !== 'at_least' || missionCountMin === '' || missionCountMin == null) return null;
  const min = Number(missionCountMin);
  return Number.isInteger(min) && min >= 0 && min <= MAX_MISSION_COUNT_MIN ? min : null;
};

export const companiesQuery = ({ tab = 'prospects', search = '', filters = {}, page = 1 }) => {
  const missionCountMin = missionCountMinOf(filters);
  return {
    tab,
    search: search.trim().slice(0, MAX_SEARCH_LENGTH),
    page,
    filters: {
      departments: filters.departments ?? [],
      filterCategory: filters.category ? String(filters.category) : '',
      supplierSearch: filters.suppliers ?? [],
      missionCountMode: filters.missionCount || '',
      ...(missionCountMin !== null ? { missionCountMin } : {}),
      incompleteRemuneration: Boolean(filters.incompleteRemuneration),
      // The sizes of the client (constants/clientSizes); none, every size.
      sizes: filters.sizes ?? [],
      action: filters.action ?? NO_CRM_FILTERS.action,
      commercial: filters.commercial ?? NO_CRM_FILTERS.commercial,
      // The queue « à attribuer »: the prospects tab only.
      unassigned: tab === 'prospects' && filters.unassigned === true,
    },
  };
};

// A row of the api for the screen: `lostAt` is an instant there, a day
// (« 2026-09-02 ») for describeCompany.
const companyRow = (row) => ({ ...row, lostAt: row.lostAt ? isoDay(new Date(row.lostAt)) : null });

// The thresholds come with every answer, a page without a client included;
// until the first one, there is nothing to tell in the tooltip of a class.
const thresholdsOf = (answer) => {
  const { grand_compte: grandCompte, pme } = answer?.sizeThresholds ?? {};
  return Number.isFinite(grandCompte) && Number.isFinite(pme) ? { grand_compte: grandCompte, pme } : null;
};

/**
 * What the page reads of { data, page, totalPages, counts, sizeThresholds }:
 * the companies of the page, the count of every tab and of the one shown, the
 * page (from 1) out of how many, and the minimum ETP of each class
 * ({ grand_compte, pme } | null).
 */
export const companiesPage = (answer, tab) => {
  const counts = { prospects: 0, new: 0, old: 0, lost: 0, ...answer?.counts };
  return {
    companies: (answer?.data ?? []).map(companyRow),
    counts,
    count: counts[tab] ?? 0,
    page: answer?.page ?? 1,
    pages: Math.max(1, answer?.totalPages ?? 1),
    perPage: COMPANIES_PER_PAGE,
    sizeThresholds: thresholdsOf(answer),
  };
};
